/**
 * Cattle and exotic livestock. Cattle sales sell per head with bred/open
 * status doing the selling; exotics are species-led and paperwork-heavy
 * (state permits vary by species and destination - the platform tracks the
 * documents, the sale company owns legality, per the terms).
 */

import { z } from 'zod';
import type { LotKindDefinition } from '../registry.js';

export const cattle: LotKindDefinition = {
  kind: 'cattle',
  label: 'Cattle',
  attributes: z
    .object({
      class: z.enum(['cow', 'bull', 'steer', 'heifer', 'pair']).optional(),
      breed: z.string().optional(),
      weightLbs: z.number().positive().optional(),
      ageMonths: z.number().int().positive().optional(),
      bred: z.boolean().optional(),
      dueDate: z.string().optional(),
      polled: z.boolean().optional(),
      registration: z
        .object({ registry: z.string().min(1), number: z.string().min(1) })
        .strict()
        .optional(),
    })
    .strict(),
  releaseChecklist: [
    { docType: 'bangs_tb_test', label: "Bang's / TB test", required: true },
    { docType: 'health_cert', label: 'Health certificate (CVI)', required: true },
    {
      docType: 'registration_papers',
      label: 'Registration papers',
      required: false,
      requiredWhen: (a) => a['registration'] !== undefined,
    },
  ],
  facets: [
    { path: 'class', label: 'Class', type: 'enum' },
    { path: 'breed', label: 'Breed', type: 'string' },
    { path: 'bred', label: 'Bred', type: 'boolean' },
    { path: 'weightLbs', label: 'Weight', type: 'number', unit: 'lbs' },
  ],
  // The lot page reads more than the sidebar filters on: age and the due
  // date sell a bred cow, neither is a filter.
  details: [
    { path: 'class', label: 'Class', type: 'enum' },
    { path: 'breed', label: 'Breed', type: 'string' },
    { path: 'ageMonths', label: 'Age', type: 'number', unit: 'months' },
    { path: 'weightLbs', label: 'Weight', type: 'number', unit: 'lbs' },
    { path: 'bred', label: 'Bred', type: 'boolean' },
    { path: 'dueDate', label: 'Due', type: 'string' },
    { path: 'polled', label: 'Polled', type: 'boolean' },
    { path: 'registration.registry', label: 'Registry', type: 'string' },
  ],
};

export const exotic: LotKindDefinition = {
  kind: 'exotic',
  label: 'Exotic animal',
  attributes: z
    .object({
      /** The one required attribute in the whole registry: an exotic lot
       *  without a species is unlistable and unsearchable. */
      species: z.string().min(1),
      commonName: z.string().optional(),
      sex: z.enum(['male', 'female', 'unknown']).optional(),
      ageMonths: z.number().int().positive().optional(),
      captiveBred: z.boolean().optional(),
      usdaLicensedFacility: z.boolean().optional(),
      tame: z.boolean().optional(),
    })
    .strict(),
  releaseChecklist: [
    { docType: 'state_permit', label: 'State possession/transport permit', required: true },
    { docType: 'health_cert', label: 'Health certificate (CVI)', required: true },
    {
      docType: 'usda_paperwork',
      label: 'USDA facility paperwork',
      required: false,
      requiredWhen: (a) => a['usdaLicensedFacility'] === true,
    },
  ],
  facets: [
    { path: 'species', label: 'Species', type: 'string' },
    { path: 'sex', label: 'Sex', type: 'enum' },
    { path: 'captiveBred', label: 'Captive bred', type: 'boolean' },
  ],
  details: [
    { path: 'species', label: 'Species', type: 'string' },
    { path: 'commonName', label: 'Common name', type: 'string' },
    { path: 'sex', label: 'Sex', type: 'enum' },
    { path: 'ageMonths', label: 'Age', type: 'number', unit: 'months' },
    { path: 'captiveBred', label: 'Captive bred', type: 'boolean' },
    { path: 'tame', label: 'Tame', type: 'boolean' },
    { path: 'usdaLicensedFacility', label: 'USDA licensed facility', type: 'boolean' },
  ],
};
